import React from 'react';
import type {IconName, IconPrefix} from '@fortawesome/fontawesome-common-types';

import {Icon, Text} from '@albert-io/atomic';

interface InviteMethod {
  modalKey: string;
  title: string;
  description: string;
  icon: IconName | [IconPrefix, IconName];
  iconStyle: 'light' | 'regular';
  iconClassName: string;
  testId: string;
}

const METHODS: InviteMethod[] = [
  {
    modalKey: 'LMS',
    title: 'Import from LMS',
    description:
      'Import students from Canvas, Schoology or Brightspace and unlock assignment and grade syncing.',
    icon: 'school',
    iconStyle: 'light',
    iconClassName: 'background-grape-subtle content-grape',
    testId: 'invite-card__from-lms'
  },
  {
    modalKey: 'googleClassroom',
    title: 'Import from Google Classroom',
    description: 'Import your rosters from Google and unlock assignment posting.',
    icon: ['fab', 'google'],
    iconStyle: 'regular',
    iconClassName: 'background-kiwi-subtle content-kiwi',
    testId: 'invite-card__from-google'
  },
  {
    modalKey: 'classCode',
    title: 'Display class code',
    description: 'Give students a code they can use to join this class.',
    icon: 'barcode',
    iconStyle: 'light',
    iconClassName: 'background-clementine-subtle content-clementine',
    testId: 'invite-card__by-code'
  },
  {
    modalKey: 'email',
    title: 'Invite by email',
    description: 'Send invites to one or multiple student emails.',
    icon: 'envelope',
    iconStyle: 'light',
    iconClassName: 'background-dragonfruit-subtle content-dragonfruit',
    testId: 'invite-card__by-email'
  }
];

interface Props {
  onShowModal: (modal: string) => void;
}

export const InviteMethodCards = ({onShowModal}: Props) => (
  <ul className='m-x0 grid list-none grid-cols-1 gap-x2 p-x0 m:grid-cols-2'>
    {METHODS.map((method) => (
      <li key={method.modalKey}>
        {/* Raw button: card-layout tile; focus ring mirrors atomic $outline-focus width with the
            semantic brand color. */}
        <button
          className='flex h-full w-full cursor-pointer items-center gap-x1-half rounded-x1-half border border-border-primary background-primary p-x3 text-left hover:background-primary-hover focus-visible:[outline:3px_solid_var(--color-content-brand)] active:background-primary-pressed'
          data-testid={method.testId}
          onClick={() => onShowModal(method.modalKey)}
          type='button'
        >
          <div className='flex h-full grow flex-col items-start justify-between'>
            <span
              className={`flex h-x5 w-x5 items-center justify-center rounded-x1 text-xl ${method.iconClassName}`}
            >
              <Icon
                color='inherit'
                icon={method.icon}
                iconStyle={method.iconStyle}
                size='inherit'
              />
            </span>
            <div className='flex flex-col gap-x0-quarter pt-x2'>
              <Text>{method.title}</Text>
              <Text color='tertiary' size='s'>
                {method.description}
              </Text>
            </div>
          </div>
          <Icon className='shrink-0 content-tertiary' icon='chevron-right' iconStyle='regular' />
        </button>
      </li>
    ))}
  </ul>
);
